/**
 * Криптографический сервис для проверки JWT и Ed25519 подписей
 * Используем tweetnacl - готовую библиотеку без необходимости настройки SHA512
 * 
 * Поддерживает два формата токенов:
 * 1. JWT (legacy, 320 байт)
 * 2. Binary (новый, 75 байт)
 */
import nacl from 'tweetnacl'
import { decodeJwt, type JWTPayload } from 'jose'
import { EmployeesRepo } from '../offline/employeesRepo'
import { EmployeesService } from './employeesService'
import { SyncQueue } from './syncQueue'
import {
  TokenFormatDetector,
  verifyBinaryToken
} from '../utils/binaryToken'
import { db } from '../offline/db'
import { LogsRepo } from '../offline/logsRepo'

console.log('[CryptoService] Using tweetnacl for Ed25519 signature verification')

export interface QRTokenPayload extends JWTPayload {
  employee_id: string
  role: 'worker' | 'admin'
  ts: number // timestamp (time slot)
  nonce?: string
}

export interface QRVerificationResult {
  valid: boolean
  payload?: QRTokenPayload
  error?: string
  employeeId?: string
  role?: 'worker' | 'admin'
  eventType?: 'check_in' | 'check_out' // Тип события для отображения результата
}

/**
 * Проверяет JWT токен из QR-кода
 */
export class CryptoService {
  /**
   * Декодирует JWT без проверки подписи (для получения payload)
   */
  static decodeJWT(token: string): QRTokenPayload | null {
    try {
      // Логируем начало и конец токена для диагностики
      console.log(`[CryptoService] Decoding JWT, length: ${token.length}, start: ${token.substring(0, 30)}...`)
      const decoded = decodeJwt(token) as QRTokenPayload
      return decoded
    } catch (error) {
      console.error('[CryptoService] Failed to decode JWT:', error)
      console.error('[CryptoService] Invalid token:', token)
      return null
    }
  }

  /**
   * Проверяет подпись JWT используя публичный ключ сотрудника
   */
  static async verifyJWTSignature(
    token: string,
    publicKey: string
  ): Promise<boolean> {
    try {
      // JWT формат: header.payload.signature
      const parts = token.split('.')
      if (parts.length !== 3) {
        return false
      }

      const [header, payload, signature] = parts

      console.log(`[CryptoService] JWT parts - header length: ${header.length}, payload length: ${payload.length}, signature length: ${signature.length}`)
      
      // Декодируем signature из base64url
      const signatureBytes = this.base64UrlDecode(signature)
      console.log(`[CryptoService] Signature bytes length: ${signatureBytes.length}, first bytes: ${Array.from(signatureBytes.slice(0, 8)).map(b => b.toString(16).padStart(2, '0')).join(' ')}`)

      // Конвертируем публичный ключ из строки в Uint8Array (поддерживает hex и PEM)
      const publicKeyBytes = this.publicKeyToBytes(publicKey)
      
      console.log(`[CryptoService] Public key format: ${publicKey.includes('BEGIN') ? 'PEM' : 'hex'}, length: ${publicKeyBytes.length} bytes`)
      
      if (publicKeyBytes.length !== 32) {
        throw new Error(`Invalid Ed25519 public key length: expected 32 bytes, got ${publicKeyBytes.length}`)
      }

      // Формируем сообщение для проверки: header.payload
      const message = `${header}.${payload}`
      const messageBytes = new TextEncoder().encode(message)
      console.log(`[CryptoService] Message length: ${messageBytes.length}, first bytes: ${Array.from(messageBytes.slice(0, 20)).map(b => b.toString(16).padStart(2, '0')).join(' ')}`)

      // Проверяем подпись Ed25519 используя tweetnacl
      // Ed25519 подписи в JWT должны быть 64 байта
      if (signatureBytes.length !== 64) {
        console.error(`[CryptoService] Invalid signature length: expected 64 bytes, got ${signatureBytes.length}`)
        return false
      }
      
      const verified = nacl.sign.detached.verify(messageBytes, signatureBytes, publicKeyBytes)
      
      console.log(`[CryptoService] Signature verification: ${verified ? 'VALID' : 'INVALID'}`)
      
      return verified
    } catch (error) {
      console.error('Failed to verify JWT signature:', error)
      return false
    }
  }

  /**
   * Проверяет таймслот (допуск ±30 секунд)
   */
  static validateTimeSlot(ts: number, toleranceSeconds: number = 45): boolean {
    const now = Math.floor(Date.now() / 1000)
    const diff = Math.abs(now - ts)
    
    console.log('[CryptoService] Time slot validation:', {
      ts,
      now,
      diff,
      tolerance: toleranceSeconds,
      valid: diff <= toleranceSeconds
    })
    
    return diff <= toleranceSeconds
  }

  /**
   * Полная проверка QR-кода (JWT токена)
   * @param token - JWT токен из QR-кода
   * @param siteId - ID объекта (для получения публичного ключа через API при необходимости)
   */
  static async verifyQRToken(
    token: string,
    siteId?: string
  ): Promise<QRVerificationResult> {
    try {
      // Определяем формат токена по длине
      if (token.length === 32 && /^[0-9a-fA-F]+$/.test(token)) {
        // TOTP токен (16 байт = 32 hex)
        console.log('[CryptoService] Detected TOTP token format (32 hex chars)')
        return this.verifyTOTPQRToken(token)
      }
      
      if (TokenFormatDetector.isBinaryToken(token)) {
        console.log('[CryptoService] Detected binary token format, using binary verification')
        return this.verifyBinaryQRToken(token)
      }
      
      // Если не binary и не TOTP, пробуем JWT
      console.log('[CryptoService] Attempting JWT verification')
      
      // 1. Декодируем JWT
      const payload = this.decodeJWT(token)
      if (!payload) {
        return {
          valid: false,
          error: 'Invalid JWT format',
        }
      }

      // 2. Проверяем обязательные поля
      if (!payload.employee_id || !payload.role || !payload.ts) {
        return {
          valid: false,
          error: 'Missing required fields in JWT payload',
        }
      }

      // 3. Проверяем роль
      if (payload.role !== 'worker' && payload.role !== 'admin') {
        return {
          valid: false,
          error: 'Invalid role',
        }
      }

      // 4. Проверяем таймслот
      if (!this.validateTimeSlot(payload.ts)) {
        return {
          valid: false,
          error: 'Token expired (time slot validation failed)',
        }
      }

      // 5. Получаем публичный ключ сотрудника из кеша или API
      let publicKey: string | null = await EmployeesRepo.getPublicKey(payload.employee_id)
      let keySource = 'cache'
      
      // Если ключа нет в кеше и есть siteId, пытаемся получить через API
      if (!publicKey && siteId) {
        console.log(`[CryptoService] Public key not in cache, fetching from API for employee ${payload.employee_id}`)
        try {
          publicKey = await EmployeesService.getPublicKey(payload.employee_id, siteId)
          keySource = 'api'
          console.log(`[CryptoService] Public key fetched from API: ${publicKey ? 'success' : 'not found'}`)
        } catch (error) {
          console.error('[CryptoService] Failed to fetch public key from API:', error)
          return {
            valid: false,
            error: `Failed to fetch public key from API: ${error instanceof Error ? error.message : 'Unknown error'}`,
            employeeId: payload.employee_id,
          }
        }
      }
      
      if (!publicKey) {
        console.error(`[CryptoService] Public key not found for employee ${payload.employee_id}, siteId: ${siteId}`)
        return {
          valid: false,
          error: 'Employee public key not found in cache or API',
          employeeId: payload.employee_id,
        }
      }

      console.log(`[CryptoService] Verifying signature with key from ${keySource}, key length: ${publicKey.length}`)

      // 6. Проверяем подпись
      const signatureValid = await this.verifyJWTSignature(
        token,
        publicKey
      )
      if (!signatureValid) {
        console.error(`[CryptoService] Signature verification failed for employee ${payload.employee_id}`)
        console.error(`[CryptoService] Token preview: ${token.substring(0, 50)}...`)
        console.error(`[CryptoService] Public key preview: ${publicKey.substring(0, 50)}...`)
        return {
          valid: false,
          error: 'Invalid signature',
          employeeId: payload.employee_id,
        }
      }

      // 7. Все проверки пройдены
      return {
        valid: true,
        payload,
        employeeId: payload.employee_id,
        role: payload.role,
      }
    } catch (error) {
      console.error('QR token verification failed:', error)
      return {
        valid: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      }
    }
  }

  /**
   * Вычисляет hash payload для события
   */
  static async calculatePayloadHash(payload: {
    employee_id: string
    site_id: string
    device_id: string
    event_type: 'check_in' | 'check_out'
    happened_at: string
    qr_token: string
  }): Promise<string> {
    const data = `${payload.employee_id}:${payload.site_id}:${payload.device_id}:${payload.event_type}:${payload.happened_at}:${payload.qr_token}`
    // Используем Web Crypto API для SHA-256
    return await this.sha256(data)
  }

  /**
   * SHA-256 хеширование
   */
  private static async sha256(data: string): Promise<string> {
    const encoder = new TextEncoder()
    const dataBuffer = encoder.encode(data)
    const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
  }

  /**
   * Декодирует base64url в Uint8Array
   */
  private static base64UrlDecode(str: string): Uint8Array {
    // Заменяем base64url символы на base64
    let base64 = str.replace(/-/g, '+').replace(/_/g, '/')
    
    // Добавляем padding если нужно
    while (base64.length % 4) {
      base64 += '='
    }
    
    // Декодируем
    const binaryString = atob(base64)
    const bytes = new Uint8Array(binaryString.length)
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }
    return bytes
  }

  /**
   * Конвертирует hex строку в Uint8Array
   */
  private static hexToBytes(hex: string): Uint8Array {
    const bytes = new Uint8Array(hex.length / 2)
    for (let i = 0; i < hex.length; i += 2) {
      bytes[i / 2] = parseInt(hex.substr(i, 2), 16)
    }
    return bytes
  }

  /**
   * Извлекает raw Ed25519 публичный ключ из PEM формата
   * PEM формат содержит SubjectPublicKeyInfo (ASN.1 DER)
   * Структура: SEQUENCE { SEQUENCE { algorithm }, BIT STRING { 0x00, 32-byte key } }
   */
  private static pemToRawPublicKey(pem: string): Uint8Array {
    // Удаляем заголовки PEM
    const base64 = pem
      .replace(/-----BEGIN PUBLIC KEY-----/g, '')
      .replace(/-----END PUBLIC KEY-----/g, '')
      .replace(/\s/g, '')
    
    // Декодируем base64
    const binaryString = atob(base64)
    const pemBytes = new Uint8Array(binaryString.length)
    for (let i = 0; i < binaryString.length; i++) {
      pemBytes[i] = binaryString.charCodeAt(i)
    }
    
    console.log(`[CryptoService] PEM bytes length: ${pemBytes.length}`)
    console.log(`[CryptoService] PEM bytes (hex): ${Array.from(pemBytes.slice(0, 20)).map(b => b.toString(16).padStart(2, '0')).join(' ')}...`)
    
    // SubjectPublicKeyInfo для Ed25519 имеет структуру ASN.1 DER:
    // 0x30 [length] SEQUENCE {
    //   0x30 [length] SEQUENCE { algorithm OID, parameters }
    //   0x03 [length] BIT STRING { 0x00 (unused bits), 32-byte key }
    // }
    
    // Ищем BIT STRING (0x03) и извлекаем ключ
    // BIT STRING обычно находится после алгоритма
    let bitStringIndex = -1
    for (let i = 0; i < pemBytes.length - 5; i++) {
      if (pemBytes[i] === 0x03) { // BIT STRING tag
        // Проверяем что следующий байт - это длина (обычно 0x21 = 33 байта: 1 байт unused + 32 байта ключа)
        const length = pemBytes[i + 1]
        if (length === 0x21 || length === 0x23) { // 33 или 35 байта (может быть с длиной в 2 байта)
          bitStringIndex = i
          break
        }
      }
    }
    
    if (bitStringIndex === -1) {
      // Fallback: пробуем найти последние 33 байта (1 байт unused + 32 байта ключа)
      console.warn('[CryptoService] BIT STRING not found, using fallback: last 33 bytes')
      if (pemBytes.length >= 33) {
        // Пропускаем первый байт (unused bits) и берем следующие 32 байта
        const rawKey = pemBytes.slice(-33).slice(1)
        console.log(`[CryptoService] Extracted key (fallback): ${Array.from(rawKey.slice(0, 8)).map(b => b.toString(16).padStart(2, '0')).join(' ')}...`)
        return rawKey
      }
      throw new Error(`Invalid PEM format: cannot find BIT STRING, total length: ${pemBytes.length}`)
    }
    
    // Извлекаем длину BIT STRING
    const bitStringLength = pemBytes[bitStringIndex + 1]
    const unusedBits = pemBytes[bitStringIndex + 2] // Обычно 0x00
    
    console.log(`[CryptoService] Found BIT STRING at index ${bitStringIndex}, length: ${bitStringLength}, unused bits: ${unusedBits}`)
    
    // Ключ начинается после unused bits байта
    const keyStart = bitStringIndex + 3
    const keyEnd = keyStart + 32
    
    if (keyEnd > pemBytes.length) {
      throw new Error(`Invalid PEM format: BIT STRING too short, expected 32 bytes key`)
    }
    
    const rawKey = pemBytes.slice(keyStart, keyEnd)
    console.log(`[CryptoService] Extracted key: ${Array.from(rawKey.slice(0, 8)).map(b => b.toString(16).padStart(2, '0')).join(' ')}...`)
    
    return rawKey
  }

  /**
   * Конвертирует публичный ключ в raw bytes (поддерживает hex и PEM)
   */
  private static publicKeyToBytes(publicKey: string): Uint8Array {
    // Проверяем формат ключа
    if (publicKey.includes('-----BEGIN PUBLIC KEY-----')) {
      // PEM формат
      console.log('[CryptoService] Detected PEM format, extracting raw key...')
      return this.pemToRawPublicKey(publicKey)
    } else if (publicKey.length === 64 && /^[0-9a-fA-F]+$/.test(publicKey)) {
      // Hex формат (64 символа = 32 байта)
      console.log('[CryptoService] Detected hex format')
      return this.hexToBytes(publicKey)
    } else {
      throw new Error(`Unsupported public key format: length=${publicKey.length}, starts with PEM=${publicKey.includes('BEGIN')}`)
    }
  }
  
  /**
   * ============================================
   * НОВЕЙШИЙ ФОРМАТ: Валидация TOTP+HMAC токенов (16 байт)
   * ============================================
   */
  
  /**
   * Проверяет TOTP+HMAC QR токен (новый формат, 16 байт = 32 hex)
   * @param tokenHex - hex строка токена (32 символа)
   * @returns результат валидации
   */
  static async verifyTOTPQRToken(
    tokenHex: string
  ): Promise<QRVerificationResult> {
    try {
      console.log('[TOTPToken] Verifying TOTP token, hex length:', tokenHex.length)
      
      // 1. Импортируем функции
      const { verifyTOTPToken, detectTokenFormat } = await import('../utils/totpToken')
      
      // 2. Проверяем формат
      const format = detectTokenFormat(tokenHex)
      if (format !== 'totp') {
        return {
          valid: false,
          error: 'Invalid TOTP token format'
        }
      }
      
      // 3. Парсим токен для получения short_id
      const tokenBytes = new Uint8Array(tokenHex.length / 2)
      for (let i = 0; i < tokenHex.length; i += 2) {
        tokenBytes[i / 2] = parseInt(tokenHex.substring(i, i + 2), 16)
      }
      const view = new DataView(tokenBytes.buffer)
      const employeeShortId = view.getUint16(0, true) // little-endian
      
      console.log('[TOTPToken] Parsed short_id:', employeeShortId)
      
      // 4. Получаем employee_id по short_id
      let mapping = await EmployeesRepo.getEmployeeIdByShortId(employeeShortId)
      
      // 🔄 FIXED: Если сотрудник не найден, используем SyncQueue с HIGH приоритетом
      // SyncQueue гарантирует:
      // - Ожидание завершения текущей синхронизации (если идет)
      // - Отсутствие race condition при параллельных запросах
      // - Retry логику и ETag кеширование
      if (!mapping) {
        console.warn(`[TOTPToken] ⚠️ Employee not found for short_id ${employeeShortId}, requesting HIGH priority sync...`)
        
        // 🔍 DIAGNOSTIC: Собираем детальную информацию для исследования
        const diagnosticInfo = {
          short_id: employeeShortId,
          timestamp: new Date().toISOString(),
          sync_status: {
            is_syncing: EmployeesService.syncInProgress,
            last_sync: EmployeesService.lastSync?.toISOString() || 'never',
          },
          indexeddb_state: {
            total_employees: 0,
            has_mapping: !!mapping
          },
          device_info: {
            online: navigator.onLine,
            service_worker_active: !!navigator.serviceWorker?.controller,
            user_agent: navigator.userAgent
          }
        }
        
        try {
          // Получаем количество сотрудников в IndexedDB
          diagnosticInfo.indexeddb_state.total_employees = await db.employees_cache.count()
        } catch (dbError) {
          console.error('[DIAGNOSTIC] Failed to count employees:', dbError)
        }
        
        // Логируем в console и в LogsRepo для удаленного доступа
        console.error('[DIAGNOSTIC] Employee not found', diagnosticInfo)
        await LogsRepo.save('error', 'Employee not found - diagnostic info', diagnosticInfo)
        
        try {
          const syncStartTime = Date.now()
          console.log('[TOTPToken] 🔄 Starting HIGH priority sync...')
          
          // HIGH priority = немедленная синхронизация, но с ожиданием если sync уже идет
          await SyncQueue.requestSync('HIGH')
          
          const syncDuration = Date.now() - syncStartTime
          console.log(`[TOTPToken] ✅ HIGH priority sync completed in ${syncDuration}ms`)
          
          // Повторная попытка после синхронизации
          mapping = await EmployeesRepo.getEmployeeIdByShortId(employeeShortId)
          
          if (mapping) {
            console.log('[TOTPToken] ✅ Employee found after HIGH priority sync!')
            await LogsRepo.save('info', `Employee found after sync (${syncDuration}ms)`, {
              short_id: employeeShortId,
              sync_duration_ms: syncDuration
            })
          } else {
            console.warn('[TOTPToken] ⚠️ Employee still not found after sync')
            await LogsRepo.save('warn', 'Employee still not found after HIGH priority sync', {
              short_id: employeeShortId,
              sync_duration_ms: syncDuration,
              employees_count_after_sync: await db.employees_cache.count()
            })
          }
        } catch (syncError) {
          const errorMsg = syncError instanceof Error ? syncError.message : 'Unknown error'
          console.error('[TOTPToken] ❌ HIGH priority sync failed:', syncError)
          await LogsRepo.save('error', 'HIGH priority sync failed', {
            short_id: employeeShortId,
            error: errorMsg
          })
        }
      }
      
      if (!mapping) {
        return {
          valid: false,
          error: `Employee not found for short_id ${employeeShortId} (even after emergency sync)`
        }
      }
      
      const employeeId = mapping.employee_id
      console.log('[TOTPToken] Found employee:', employeeId)
      
      // 5. Получаем TOTP secret из кеша
      const totpSecret = await EmployeesRepo.getTOTPSecret(employeeId)
      if (!totpSecret) {
        return {
          valid: false,
          error: `TOTP secret not found for employee ${employeeId}`
        }
      }
      
      // 6. Верифицируем токен
      const result = await verifyTOTPToken(tokenHex, totpSecret)
      
      if (!result.valid) {
        return {
          valid: false,
          error: result.error,
          employeeId
        }
      }
      
      console.log('[TOTPToken] Token verified successfully!')
      
      // 7. Формируем payload для совместимости с остальной логикой
      const now = Math.floor(Date.now() / 1000)
      const payload: QRTokenPayload = {
        employee_id: employeeId,
        role: result.role || 'worker',
        ts: result.payload?.timestampSlot ? result.payload.timestampSlot * 15 : now,
        iat: now
      }
      
      // 8. Возвращаем результат
      return {
        valid: true,
        employeeId,
        role: result.role,
        payload
      }
    } catch (error) {
      console.error('[TOTPToken] Verification failed:', error)
      return {
        valid: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }
    }
  }

  /**
   * ============================================
   * LEGACY: Валидация бинарных токенов (73-75 байт)
   * ============================================
   */
  
  /**
   * Проверяет бинарный QR токен (legacy формат, 73-75 байт)
   * @param tokenHex - hex строка токена (146-150 символов)
   * @returns результат валидации
   */
  static async verifyBinaryQRToken(
    tokenHex: string
  ): Promise<QRVerificationResult> {
    try {
      console.log('[BinaryQR] Verifying binary token, hex length:', tokenHex.length)
      
      if (!TokenFormatDetector.isBinaryToken(tokenHex)) {
        return {
          valid: false,
          error: 'Invalid binary token format'
        }
      }
      
      // Сначала декодируем токен чтобы получить short_id (без проверки подписи)
      const { hexToBytes, decodePayload } = await import('../utils/binaryToken')
      const tokenBytes = hexToBytes(tokenHex)
      const encodedPayload = tokenBytes.slice(0, 9)
      const preliminaryPayload = decodePayload(encodedPayload)
      
      console.log('[BinaryQR] Preliminary payload:', {
        shortId: preliminaryPayload.employee_short_id,
        timestamp: preliminaryPayload.timestamp,
        role: preliminaryPayload.role
      })
      
      // Получаем employee_id и public_key по short_id из кеша
      const mapping = await EmployeesRepo.getEmployeeIdByShortId(preliminaryPayload.employee_short_id)
      
      if (!mapping) {
        return {
          valid: false,
          error: `Employee not found for short_id ${preliminaryPayload.employee_short_id}`
        }
      }
      
      const employeeUuid = mapping.employee_id
      console.log(`[BinaryQR] Found employee: ${employeeUuid}`)
      
      // Получаем публичный ключ
      let publicKeyHex = await EmployeesRepo.getPublicKey(employeeUuid)
      
      if (!publicKeyHex) {
        return {
          valid: false,
          error: `Public key not found for employee ${employeeUuid}`
        }
      }
      
      // Конвертируем PEM публичный ключ в raw hex (32 байта)
      const publicKeyBytes = this.publicKeyToBytes(publicKeyHex)
      const publicKeyHexRaw = Array.from(publicKeyBytes)
        .map(b => b.toString(16).padStart(2, '0'))
        .join('')
      
      console.log('[BinaryQR] Verifying signature...', {
        publicKeyLength: publicKeyBytes.length,
        tokenLength: tokenHex.length
      })
      
      // Проверяем подпись
      const verifiedPayload = await verifyBinaryToken(tokenHex, publicKeyHexRaw)
      
      if (!verifiedPayload) {
        return {
          valid: false,
          error: 'Signature verification failed'
        }
      }
      
      // Проверяем timestamp (15 секунд tolerance)
      const now = Math.floor(Date.now() / 1000)
      const timeDiff = Math.abs(now - verifiedPayload.timestamp)
      
      if (timeDiff > 45) { // 3 слота по 15 секунд
        return {
          valid: false,
          error: `Token expired: timestamp ${verifiedPayload.timestamp}, now ${now}, diff ${timeDiff}s`
        }
      }
      
      console.log('[BinaryQR] Token verified successfully!')
      
      // Формируем результат
      return {
        valid: true,
        payload: {
          employee_id: employeeUuid,
          role: verifiedPayload.role,
          ts: verifiedPayload.timestamp
        } as QRTokenPayload,
        employeeId: employeeUuid,
        role: verifiedPayload.role
      }
    } catch (error) {
      console.error('[BinaryQR] Verification error:', error)
      return {
        valid: false,
        error: error instanceof Error ? error.message : String(error)
      }
    }
  }
  
  /**
   * Универсальная проверка QR (автоматически определяет формат)
   * Поддерживает JWT (legacy) и Binary (новый)
   * 
   * @param token - токен (JWT string или hex string)
   * @param siteId - ID объекта (для получения ключей через API)
   * @returns результат валидации
   */
  static async verifyQRTokenAuto(
    token: string,
    siteId?: string
  ): Promise<QRVerificationResult> {
    // Определяем формат токена
    if (TokenFormatDetector.isBinaryToken(token)) {
      console.log('[CryptoService] Detected binary token format')
      return this.verifyBinaryQRToken(token)
    } else if (TokenFormatDetector.isJWT(token)) {
      console.log('[CryptoService] Detected JWT format')
      return this.verifyQRToken(token, siteId)
    } else {
      return {
        valid: false,
        error: 'Unknown token format'
      }
    }
  }
}

